/* eslint-disable no-unused-vars */
import styled, { css } from 'styled-components';
import { Palette } from 'config';
import { useSelector, useDispatch } from 'react-redux';
import { useId, useRef, useMemo, useCallback } from 'react';
import { CustomInput, Label, DropDownWrapper, Each } from 'components';
import { useComputed, useSignal } from '@preact/signals-react';

export const ImageSizeInput = ({
  data,
  currentSelectedSize$,
  previewSectionImage$,
  isDisabled,
}) => {
  const inputId = useId();
  const dropDownIsOpen$ = useSignal(false);
  const banners = useSelector((state) => state?.creatives?.selectedCreative?.banners);
  const btnRef = useRef(null);

  const isShowElement = useCallback(
    (imageType) => {
      const res = banners.findIndex(
        ({ image_type }) => image_type.toString() === imageType.toString(),
      );
      if (res === -1) return false;
      return true;
    },
    [banners],
  );

  const inputValue$ = useComputed(() => {
    if (!currentSelectedSize$.value) {
      return '';
    } else {
      return `${currentSelectedSize$.value?.imageSize} pixels`;
    }
  });

  const allSizesWasSelected = useMemo(() => {
    const selectedBanners = new Set(banners.map(({ image_type }) => +image_type));
    const res = data.every(({ imageType }) => selectedBanners.has(imageType));
    return res;
  }, [data, banners]);

  const handleOpenSizesList = () => {
    if (isDisabled) return;

    previewSectionImage$.value = null;
    dropDownIsOpen$.value = true;
  };

  return (
    <div style={{ position: 'relative' }} ref={btnRef}>
      <Label text='Image size:' variant='label' style={{ marginBottom: '8px' }} />
      <div onClick={handleOpenSizesList}>
        <CustomInput
          placeholder={
            allSizesWasSelected
              ? 'All possible sizes have already been selected.'
              : 'Select Image Size'
          }
          value={inputValue$.value}
          autoComplete='off'
          id={inputId}
          name={inputId}
          isDropdown={!allSizesWasSelected && !isDisabled}
          arrowIconIsActive={dropDownIsOpen$.value}
          disabled={allSizesWasSelected || isDisabled}
          inputStyle={{
            minHeight: '48px',
            fontSize: '16px',
          }}
          onChange={() => {}}
        />
      </div>
      <DropDownWrapper
        isOpen$={!allSizesWasSelected ? dropDownIsOpen$ : { value: false }}
        targetRef={btnRef}
        style={{
          top: '85px',
          left: '0px',
        }}
        outSideClickAction={() => {}}
      >
        <ListContainer>
          {
            <Each
              fieldOfIndex='imageType'
              of={data}
              render={(item, index) => {
                return (
                  <SizeItem
                    isShowElement={isShowElement(item.imageType)}
                    dropDownIsOpen$={dropDownIsOpen$}
                    item={item}
                    currentSelectedSize$={currentSelectedSize$}
                  />
                );
              }}
            />
          }
        </ListContainer>
      </DropDownWrapper>
    </div>
  );
};

const SizeItem = ({ item, currentSelectedSize$, dropDownIsOpen$, isShowElement }) => {
  const isSelected = item.imageType === currentSelectedSize$.value?.imageType;

  return (
    <SizeItemBox
      onClick={() => {
        currentSelectedSize$.value = item;
        dropDownIsOpen$.value = false;
      }}
      isSelected={isSelected}
      isShowElement={isShowElement}
    >
      {item.imageSize} pixels
    </SizeItemBox>
  );
};

const SizeItemBox = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 40px;
  line-height: 40px;
  padding: 0 8px;
  font-size: 14px;
  border: 1px solid transparent;
  :hover {
    border-radius: 4px;
    background: ${Palette.PrimaryGray._100};
  }
  ${({ isSelected }) =>
    isSelected &&
    css`
      border-radius: 4px;
      border: 1px solid ${Palette.Primary._300};
      background: ${Palette.Primary._000};
      color: ${Palette.Primary._500};
    `}
  ${({ isShowElement }) =>
    isShowElement &&
    css`
      display: none;
    `}
`;
const ListContainer = styled.div`
  max-height: 120px;
  overflow-y: auto;
  ::-webkit-scrollbar {
    width: 6px;
    background-color: #fff;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 8px;
  }
`;
